import { Response } from 'datx-jsonapi';
import { useResource, useResourceRelationship, useResourcesInfinite } from 'datx-jsonapi-react';
import { FC, useEffect, useMemo, useState } from 'react';

import { FilterCombobox } from '@/components/ui/features/hubs-publishers/PublisherFilter/components/FilterCombobox';
import { useLibraryFilters } from '@/components/ui/features/library/LibraryFilter/LibraryFilter.hooks';
import { LibraryFilter } from '@/enums/LibraryFilter';
import { useQueryParams } from '@/hooks/useQueryParams';
import { IAlgoliaFilterItem } from '@/interfaces/IAlgoliaFilterItem';
import { queryEventFiltersInfinite, querySelectedConferenceFilters } from '@/queries/algolia-filter-item';
import { getHubBySlug, getHubsSubtree } from '@/queries/hubs';
import { AlgoliaFilterItem } from '@/resources/AlgoliaFilterItem';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { StringParam } from '@/utils/url-query-string';

const defaultSubject: IAlgoliaFilterItem = { label: 'All subjects', value: null, amount: 0 };

export const FilterSubjects: FC = () => {
  const [subjectSearch, setSubjectSearch] = useState(null);

  const { filters, change } = useLibraryFilters({
    [LibraryFilter.Subject]: StringParam,
  });

  const { slug, query } = useQueryParams<{ slug: string; query: string }>({
    slug: StringParam,
    query: StringParam,
  });

  const { data: hub } = useResource(getHubBySlug(slug));
  const {
    data: { subtreeIds },
  } = useResourceRelationship(getHubsSubtree(hub.id));

  const { data: selectedSubject } = useResource(
    querySelectedConferenceFilters({
      selected_resource: 'subject',
      selected_id: filters?.[LibraryFilter.Subject],
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { data, error, size, setSize } = useResourcesInfinite(
    queryEventFiltersInfinite('subjects', {
      filters: {
        query,
        subjects_query: subjectSearch,
        subject_id: subtreeIds,
      },
      pageSize: 20,
    }),
    {
      revalidateAll: false,
    }
  );

  const { items, isLoadingMore, isReachingEnd } = useMemo(
    () => extractSWRInfiniteData<AlgoliaFilterItem>(data as Array<Response<AlgoliaFilterItem>>, error, size),
    [data, error, size]
  );

  const onChange = (value) => {
    change({ [LibraryFilter.Subject]: value }, { method: 'replace', shallow: true });
  };

  return (
    <FilterCombobox
      placeholder="Search"
      itemsList={[defaultSubject, ...items]}
      selectedItem={selectedSubject || defaultSubject}
      onChangeItem={(item) => onChange(item?.value)}
      isLoadingMore={isLoadingMore && !isReachingEnd}
      onScrollToBottom={() => {
        if (!error && !isReachingEnd) {
          setSize(size + 1);
        }
      }}
      onSearchChange={(value) => setSubjectSearch(value)}
    />
  );
};
